/**
 * Sentry initialization for the PWA.
 *
 * Init runs from main.tsx BEFORE the React root mounts so the
 * ErrorBoundary the SDK ships with can wrap the whole tree.
 *
 * Vite env vars (set at build time, baked into the bundle):
 *   VITE_SENTRY_DSN              — no-op when missing
 *   VITE_SENTRY_RELEASE          — short SHA from CI; aligns with the
 *                                  API's SENTRY_RELEASE so a deploy ID
 *                                  has both halves
 *   VITE_SENTRY_TRACES_SAMPLE_RATE  — default 0.1 (10%)
 *   VITE_SENTRY_ENVIRONMENT      — 'alpha' / 'beta' / 'production'.
 *                                  Defaults to import.meta.env.MODE
 *                                  which Vite sets per build target.
 *
 * The Sentry SDK does NOT phone home without a DSN — call sites can
 * `import * as Sentry from '@sentry/react'` unconditionally; capture
 * methods are no-ops when init was skipped.
 */

import * as Sentry from '@sentry/react';

export function initSentry(): boolean {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return false;

  const release = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;
  const environment =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
    import.meta.env.MODE;
  const tracesSampleRate = parseRate(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    0.1,
  );

  Sentry.init({
    dsn,
    environment,
    release,
    tracesSampleRate,

    // PII guardrails — match the API's posture. The SDK still attaches
    // browser context (URL path, user agent), which we trim below.
    sendDefaultPii: false,

    // Replays / sessions: deferred. They balloon Sentry quota and
    // we don't have a policy on field-tech video yet.
    // browserTracing turns on the RUM the sample rate was already
    // configured for (route transactions, Web Vitals) — before this the
    // tracesSampleRate collected nothing (audit quick-win).
    integrations: [Sentry.browserTracingIntegration()],

    beforeSend(event) {
      // Mask numeric path components on routes that carry PII keys.
      if (event.request?.url) {
        event.request.url = maskUrl(event.request.url);
      }
      if (event.request?.query_string) {
        event.request.query_string = '[redacted]';
      }
      return event;
    },

    beforeBreadcrumb(breadcrumb) {
      if (
        breadcrumb.category === 'fetch' &&
        typeof breadcrumb.data?.url === 'string'
      ) {
        breadcrumb.data.url = maskUrl(breadcrumb.data.url);
      }
      if (
        breadcrumb.category === 'navigation' &&
        typeof breadcrumb.data?.to === 'string'
      ) {
        breadcrumb.data.to = maskUrl(breadcrumb.data.to);
      }
      return breadcrumb;
    },
  });

  return true;
}

/**
 * Attach (or clear) the signed-in user on Sentry events. id + role only —
 * enough to answer "is this one tech or everyone?" during a pilot without
 * shipping emails to Sentry. Called from the auth store on session
 * set/clear; a no-op when Sentry isn't initialized.
 */
export function setSentryUser(user: { id: string; role: string } | null): void {
  Sentry.setUser(user ? { id: user.id, segment: user.role } : null);
}

function maskUrl(url: string): string {
  return url
    .replace(/\/customers\/\d+/g, '/customers/[id]')
    .replace(/\/sales-orders\/\d+/g, '/sales-orders/[id]')
    .replace(/\/inventory\/\d+/g, '/inventory/[id]');
}

function parseRate(raw: unknown, fallback: number): number {
  if (typeof raw !== 'string') return fallback;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0 || n > 1) return fallback;
  return n;
}
